import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CallListItem {
  id: string;
  created_at: string;
  direction: string;
  status: string;
  displayStatus?: string;
  duration_seconds: number | null;
  from_e164: string | null;
  to_e164: string | null;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    contact_type: string | null;
    primary_phone: string | null;
    deals?: { id: string; stage: string }[];
  } | null;
  deal: {
    id: string;
    stage: string;
  } | null;
  hasActiveDeal?: boolean;
  identifiedUserId?: string | null;
}

export interface CallsListFilters {
  startDate?: Date;
  endDate?: Date;
  userIds?: string[];
  direction?: 'inbound' | 'outbound' | null;
  contactTypes?: string[];
  hasActiveDeal?: boolean | null;
}

const OUTBOUND_DIRECTIONS = ['outbound', 'outbound-api', 'outbound-dial'];

// Deduplication - group retries to same contact within short interval
const deduplicateCalls = (calls: CallListItem[], thresholdMinutes = 2): CallListItem[] => {
  if (!calls.length) return calls;

  const thresholdMs = thresholdMinutes * 60 * 1000;
  const result: CallListItem[] = [];
  let currentGroup: CallListItem[] = [];

  const sortedCalls = [...calls].sort((a, b) => {
    const contactCompare = (a.contact?.id || '').localeCompare(b.contact?.id || '');
    if (contactCompare !== 0) return contactCompare;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  for (const call of sortedCalls) {
    const lastCall = currentGroup[currentGroup.length - 1];
    const isSameContact = lastCall && call.contact?.id && lastCall.contact?.id === call.contact.id;
    const timeDiff = lastCall
      ? Math.abs(new Date(call.created_at).getTime() - new Date(lastCall.created_at).getTime())
      : Infinity;

    if (isSameContact && timeDiff <= thresholdMs) {
      currentGroup.push(call);
    } else {
      if (currentGroup.length > 0) {
        const best = currentGroup.reduce((b, c) =>
          (c.duration_seconds || 0) > (b.duration_seconds || 0) ? c : b
        );
        result.push(best);
      }
      currentGroup = [call];
    }
  }

  if (currentGroup.length > 0) {
    const best = currentGroup.reduce((b, c) =>
      (c.duration_seconds || 0) > (b.duration_seconds || 0) ? c : b
    );
    result.push(best);
  }

  return result.sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
};

/**
 * Build user identity maps for call attribution.
 * Returns phone→appUserId, authId→appUserId, and a set that expands
 * each appUserId to include its legacy linked IDs.
 */
async function buildUserMaps() {
  const [{ data: appUsers }, { data: identityLinks }] = await Promise.all([
    supabase
      .from('app_users')
      .select('id, twilio_phone_number, auth_user_id')
      .not('twilio_phone_number', 'is', null),
    supabase
      .from('user_identity_links')
      .select('legacy_user_id, current_user_id'),
  ]);

  const phoneToUser = new Map<string, string>(
    appUsers?.map(u => [u.twilio_phone_number!, u.id]) || []
  );
  const authIdToUser = new Map<string, string>(
    appUsers?.filter(u => u.auth_user_id).map(u => [u.auth_user_id!, u.id]) || []
  );

  // Build a map: currentUserId → Set of all IDs that belong to this identity
  // (includes legacy IDs and the current ID itself)
  const identityExpansion = new Map<string, Set<string>>();
  for (const link of identityLinks || []) {
    if (!identityExpansion.has(link.current_user_id)) {
      identityExpansion.set(link.current_user_id, new Set([link.current_user_id]));
    }
    identityExpansion.get(link.current_user_id)!.add(link.legacy_user_id);
  }

  return { phoneToUser, authIdToUser, identityExpansion };
}

async function fetchCalls(filters: CallsListFilters) {
  const { phoneToUser, authIdToUser, identityExpansion } = await buildUserMaps();

  const hasUserFilter = filters.userIds && filters.userIds.length > 0;
  const hasClientSideFilters =
    hasUserFilter ||
    (filters.contactTypes && filters.contactTypes.length > 0) ||
    (filters.hasActiveDeal !== null && filters.hasActiveDeal !== undefined);

  const fetchLimit = hasClientSideFilters ? 20000 : 5000;

  // Expand userIds to include legacy linked IDs for broader matching
  let expandedUserIds: Set<string> | null = null;
  if (hasUserFilter) {
    expandedUserIds = new Set<string>();
    for (const uid of filters.userIds!) {
      expandedUserIds.add(uid);
      const linked = identityExpansion.get(uid);
      if (linked) {
        for (const lid of linked) expandedUserIds.add(lid);
      }
    }
  }

  const selectQuery = `
    id, created_at, direction, status, duration_seconds,
    from_e164, to_e164, contact_id, handled_by_app_user_id, created_by,
    contact:contacts!contact_calls_contact_id_fkey(id, first_name, last_name, contact_type, primary_phone,
      deals:deals!fk_deals_contact(id, stage)
    ),
    deal:deals!calls_deal_id_fkey(id, stage)
  `;

  let baseQuery: any = (supabase.from('calls') as any)
    .select(selectQuery, { count: 'exact' })
    .eq('channel', 'voice')
    .order('created_at', { ascending: false })
    .limit(fetchLimit);

  if (filters.startDate) {
    baseQuery = baseQuery.gte('created_at', filters.startDate.toISOString());
  }
  if (filters.endDate) {
    baseQuery = baseQuery.lte('created_at', filters.endDate.toISOString());
  }

  if (filters.direction === 'outbound') {
    baseQuery = baseQuery.in('direction', OUTBOUND_DIRECTIONS);
  } else if (filters.direction === 'inbound') {
    baseQuery = baseQuery.eq('direction', 'inbound');
  }

  const { data, error: queryError, count } = await baseQuery;
  if (queryError) throw queryError;

  const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000);

  let processedCalls: CallListItem[] = (data || []).map((call: any) => {
    const callDealActive = Boolean(
      call.deal && call.deal.stage && !['won', 'lost'].includes(call.deal.stage)
    );
    const contactHasActiveDeal = Boolean(
      call.contact?.deals?.some((d: { stage: string }) =>
        d.stage && !['won', 'lost'].includes(d.stage)
      )
    );
    const hasActiveDeal = callDealActive || contactHasActiveDeal;

    // Attribution chain: handled_by → created_by(auth→app) → phone mapping
    let identifiedUserId: string | null = call.handled_by_app_user_id || null;

    if (!identifiedUserId && call.created_by) {
      identifiedUserId = authIdToUser.get(call.created_by) || null;
    }

    if (!identifiedUserId) {
      if (call.direction?.startsWith('outbound')) {
        identifiedUserId = phoneToUser.get(call.from_e164) || null;
      } else if (call.direction === 'inbound') {
        if (call.to_e164?.startsWith('client:')) {
          const authUserId = call.to_e164.replace('client:', '');
          identifiedUserId = authIdToUser.get(authUserId) || null;
        } else {
          identifiedUserId = phoneToUser.get(call.to_e164) || null;
        }
      }
    }

    let displayStatus = call.status;
    const callDate = new Date(call.created_at);
    if (call.status === 'in-progress' && callDate < fourHoursAgo) {
      displayStatus = call.duration_seconds && call.duration_seconds > 0 ? 'completed' : 'no-answer';
    }

    return {
      ...call,
      hasActiveDeal,
      displayStatus,
      identifiedUserId,
    };
  });

  // Client-side user filter using expanded IDs
  if (hasUserFilter && expandedUserIds) {
    processedCalls = processedCalls.filter((call: any) =>
      call.identifiedUserId && expandedUserIds!.has(call.identifiedUserId)
    );
  }

  // Client-side contact type filter
  if (filters.contactTypes && filters.contactTypes.length > 0) {
    processedCalls = processedCalls.filter(call =>
      call.contact && filters.contactTypes!.includes(call.contact.contact_type || '')
    );
  }

  // Client-side active deal filter
  if (filters.hasActiveDeal !== null && filters.hasActiveDeal !== undefined) {
    processedCalls = processedCalls.filter(call => call.hasActiveDeal === filters.hasActiveDeal);
  }

  // Deduplicate
  processedCalls = deduplicateCalls(processedCalls, 2);

  return {
    allCalls: processedCalls,
    serverCount: count ?? processedCalls.length,
  };
}

export function useCallsList(filters: CallsListFilters = {}) {
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const queryKey = useMemo(() => [
    'calls-list',
    filters.startDate?.toISOString() ?? null,
    filters.endDate?.toISOString() ?? null,
    filters.userIds?.join(',') ?? null,
    filters.direction ?? null,
    filters.contactTypes?.join(',') ?? null,
    filters.hasActiveDeal ?? null,
  ], [
    filters.startDate, filters.endDate,
    filters.userIds, filters.direction,
    filters.contactTypes, filters.hasActiveDeal,
  ]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchCalls(filters),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const allCalls = data?.allCalls ?? [];
  const totalCount = allCalls.length;

  const calls = useMemo(() => {
    const start = (page - 1) * pageSize;
    return allCalls.slice(start, start + pageSize);
  }, [allCalls, page, pageSize]);

  // Reset page when filters change
  useMemo(() => {
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey.join('|')]);

  return {
    calls,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    totalCount,
    page,
    setPage,
    pageSize,
    refresh: refetch,
  };
}
